import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  useMediaQuery,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Chip,
  Divider,
  Switch,
  Slider,
  FormHelperText,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Container } from 'reactstrap';

import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const StyledTableCell = styled(TableCell)({
  color: 'white !important',
  border: 'solid 1px #dee2e6',
  fontWeight: '800',
  textAlign: 'center',
  backgroundColor: '#47A992',
  padding: 'none',
  whiteSpace: 'nowrap',
});

const FabricDefectPointInitialValue = {
  DefectPointId: 0,
  LengthOfDefect: [0, 10],
  Point: '',
  Active: 'yes',
};

const filterInitialValue = {
  searchTerm: '',
  Active: true,
};

const FabricDefectPoints = () => {
  const [filterData, setFilterData] = useState(filterInitialValue);
  const [fabricDefectPointTableData, setFabricDefectPointTableData] = useState([]);
  const [fabricDefectPoints, setFabricDefectPoints] = useState([]);
  const [UserId, setUserId] = useState(null);

  const [initialValue, setInitialValue] = useState(FabricDefectPointInitialValue);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  // snackbar context
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // api fetch data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    getAllFabricDefectPoint();
  }, []);

  const fabricDefectPointValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        LengthOfDefect: Yup.array()
          .of(Yup.number())
          .test('unique-range', 'This Length of Defect range already exists', (value) => {
            if (!Array.isArray(value) || value.length !== 2) return true;

            const [inputMin, inputMax] = value;

            const isDuplicate = fabricDefectPoints.some((data) => {
              if (data.DefectPointId === initialValue.DefectPointId) return false;
              const [min, max] = data.LengthOfDefect.split('-').map(Number);
              return min === inputMin && max === inputMax;
            });

            return !isDuplicate;
          }),
        Point: Yup.number()
          .min(1, ({ min }) => `Minimum value should be ${min} `)
          .max(100, ({ max }) => `Cannot exceed more than ${max} `)
          .required('Enter Fabric Defect Point'),
        Active: Yup.string().oneOf(['yes', 'No'], 'Please select Yes or No').required('Please select Yes or No'),
      }),
    [initialValue, fabricDefectPoints]
  );

  async function getAllFabricDefectPoint() {
    try {
      showLoading();
      const response = await axios.get('Master/FabricDefectPoints/GetAllFabricDefectPoints');
      setFabricDefectPoints(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  async function InsertFabricDefectPoint(values) {
    try {
      showLoading();
      const { LengthOfDefect, ...remainingValues } = values;
      await axios.post('Master/FabricDefectPoints/AddFabricDefectPoint', {
        ...remainingValues,
        LengthOfDefect: `${LengthOfDefect[0]} - ${LengthOfDefect[1]}`,
        UserId,
      });
      getAllFabricDefectPoint();
      setOpen(false);
      openSnackBar('Fabric Defect Point Added Successfully!');
      // setFilterData(filterInitialValue);
      setEdit(false);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  const UpdateFabricDefectPoint = async (values) => {
    try {
      showLoading();
      const { LengthOfDefect, ...remainingValues } = values;
      await axios.post(`Master/FabricDefectPoints/UpdateFabricDefectPoint`, {
        ...remainingValues,
        LengthOfDefect: `${LengthOfDefect[0]} - ${LengthOfDefect[1]}`,
        UserId,
      });
      getAllFabricDefectPoint();
      setOpen(false);
      setEdit(false);
      setInitialValue(FabricDefectPointInitialValue);
      openSnackBar('Fabric Defect Point Updated Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  };

  const DeleteFabricDefectPoint = async () => {
    try {
      showLoading();
      await axios.delete(`Master/FabricDefectPoints/DeleteFabricDefectPoint/${deleteId}`);
      const newFabricDefectPoint = fabricDefectPoints.filter(
        (data) => data.DefectPointId !== deleteId
      );
      setFabricDefectPoints(newFabricDefectPoint);
      setFabricDefectPointTableData(newFabricDefectPoint);
      openSnackBar('Fabric Defect Point Deleted Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    handleCloseDelete();
    setDeleteId(null);
    hideLoading();
  };

  const handleFilterDataChange = (name, value) => {
    setFilterData({ ...filterData, [name]: value });
  };
  useEffect(() => {
    const newTableData = fabricDefectPoints.filter(
      (data) =>
        data.LengthOfDefect.includes(filterData.searchTerm?.toLowerCase()) &&
        data.Active?.toLowerCase()?.trim() === (filterData.Active ? 'yes' : 'no')
    );
    setPage(0);
    setFabricDefectPointTableData(newTableData);
  }, [fabricDefectPoints, filterData]);

  // function handleFabricDefectPointFilterSearch(filtertext) {
  //   setFabricDefectPointFilterSearch(filtertext);

  //   const newDivsionTableData = fabricDefectPoints.filter((defectCat) =>
  //     defectCat.CategoryDescription.toLowerCase().includes(filtertext.toLowerCase())
  //   );
  //   if (page > 0) setPage(0);
  //   setFabricDefectPointTableData(newDivsionTableData);
  // }

  const handleSliderChange = (newValue, activeThumb, LengthOfDefect, setFieldValue) => {
    try {
      if (activeThumb === 0) {
        setFieldValue('LengthOfDefect', [Math.min(newValue[0], LengthOfDefect[1]), LengthOfDefect[1]]);
      } else {
        setFieldValue('LengthOfDefect', [LengthOfDefect[0], Math.max(newValue[1], LengthOfDefect[0])]);
      }
    } catch (e) {
      openSnackBar('Something went wrong while processing Inches', 'error');
    }
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  const handleCloseDialog = () => {
    setOpen(!open);
    setEdit(false);
    setInitialValue(FabricDefectPointInitialValue);
  };

  const handleEditClick = (data) => {
    setEdit(true);
    setOpen(true);
    const { LengthOfDefect, ...remainingValue } = data;
    setInitialValue({ ...remainingValue, LengthOfDefect: LengthOfDefect.split('-') });
  };

  // pagination
  const { emptyRows, totalRows, currentPage } = useMemo(() => {
    const totalRows = fabricDefectPointTableData.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const currentPage = Math.max(0, Math.min(page, totalPages - 1));
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, totalRows - page * rowsPerPage);

    return { emptyRows, totalRows, currentPage };
  }, [fabricDefectPointTableData, page, rowsPerPage]);

  return (
    <>
      <Container maxWidth={'xl'} sx={{ pt: 2 }} elevation={3}>
        {/* table header */}
        <Grid justifyContent={'space-between'} alignItems={'center'} spacing={2}>
          <Typography
            variant="h5"
            p={1}
            boxShadow={1}
            borderColor="#11235A"
            textAlign={'center'}
            borderRadius={2}
            pl={2}
            color="white"
            sx={{
              // borderRadius: 50,
              background: '#47A992',
              boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
            }}
          >
            <Typography variant="h6" color="white" className="font_style1" textAlign={'center'}>
              FABRIC DEFECT POINTS MASTER
            </Typography>
          </Typography>
        </Grid>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent={'space-between'}
          alignItems={'center'}
          spacing={2}
          my={3}
          px={3}
        >
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Search"
            size="small"
            onChange={(e) => {
              handleFilterDataChange('searchTerm', e.target.value);
            }}
            value={filterData.searchTerm}
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              Active
            </Typography>

            {/* Toggle Switch with "Yes" / "No" */}
            <FormControlLabel
              control={
                <Switch
                  checked={filterData.Active}
                  onChange={() => {
                    handleFilterDataChange('Active', !filterData.Active);
                  }}
                  color="success"
                />
              }
              label={filterData.Active ? 'Yes' : 'No'}
            />
            <Divider orientation="vertical" flexItem />
            <Button
              variant="outlined"
              color="secondary"
              sx={{ boxShadow: 1 }}
              onClick={() => setOpen(!open)}
              startIcon={<PlaylistAddIcon />}
            >
              Add Fabric Defect Points
            </Button>
          </Stack>
        </Stack>
        <TableContainer sx={{ borderRadius: '7px', maxHeight: '450px' }} component={Paper} elevation={2}>
          <Table className="table-bordered" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">S.No</StyledTableCell>
                <StyledTableCell align="left">Length Of Defect</StyledTableCell>
                <StyledTableCell align="left">Point</StyledTableCell>
                <StyledTableCell align="center">Active</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? fabricDefectPointTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : fabricDefectPointTableData
              ).map((data, i) => (
                <TableRow key={data.DefectPointId} hover>
                  <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                  <TableCell align="center">{data.LengthOfDefect} Inches</TableCell>
                  <TableCell align="center">{data.Point}</TableCell>
                  <TableCell align="center">
                    <Chip
                      color={data.Active === 'yes' ? 'success' : 'error'}
                      label={data.Active === 'yes' ? 'Yes' : 'No'}
                      size="small"
                      icon={data.Active === 'yes' ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
                    />
                  </TableCell>

                  <TableCell align="center" sx={{ padding: '0' }}>
                    <IconButton
                      aria-label="Edit"
                      onClick={() => {
                        handleEditClick(data);
                      }}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                      onClick={() => {
                        setDeleteId(data.DefectPointId);
                        setOpenDelete(true);
                      }}
                    >
                      <DeleteForeverIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={currentPage} // Use the calculated currentPage value
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0); // Reset the page when rowsPerPage changes
          }}
          mt={2}
        />
        <Grid m={2}>
          {/* add new popup form dialog box */}
          <Dialog maxWidth={'sm'} open={open} onClose={handleCloseDialog} fullWidth>
            <Container component={Paper} elevation={2} style={{ padding: '7px 14px' }}>
              <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mr={1}>
                <Typography variant="h6" textAlign={'left'} pl={2} fontWeight={600} color="primary">
                  {edit ? 'Edit' : 'Add'} Fabric Defect Points
                </Typography>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Stack sx={{ p: 1 }}>
                <Formik
                  validationSchema={fabricDefectPointValidationSchema}
                  initialValues={initialValue}
                  onSubmit={async (values, { resetForm }) => {
                    if (edit) await UpdateFabricDefectPoint(values);
                    else await InsertFabricDefectPoint(values);
                    resetForm();
                  }}
                >
                  {({ submitForm, values, errors, handleChange, handleBlur, setFieldValue, touched, isSubmitting }) => (
                    <Form>
                      <Grid container spacing={2} p={1} rowSpacing={3}>
                        <Grid item width={'100%'}>
                          <FormControl size="small" fullWidth component="fieldset">
                            <FormLabel component="legend" sx={{ mb: 5 }}>
                              Length of Defect (inch)
                            </FormLabel>
                            <Slider
                              valueLabelDisplay="on"
                              getAriaLabel={() => 'Minimum distance'}
                              value={values.LengthOfDefect}
                              error={Boolean(errors.LengthOfDefect)}
                              onChange={(event, newValue, activeThumb) =>
                                handleSliderChange(newValue, activeThumb, values.LengthOfDefect, setFieldValue)
                              }
                              valueLabelFormat={(val) => `${val}"`}
                              disableSwap
                              sx={{
                                color: errors.LengthOfDefect ? 'error.main' : 'primary.main',
                                '& .MuiSlider-valueLabel': {
                                  backgroundColor: errors.LengthOfDefect ? 'error.main' : '#1976d2',
                                  color: '#fff',
                                  fontWeight: 'bold',
                                },
                                '& .MuiSlider-thumb': {
                                  borderColor: errors.LengthOfDefect ? 'error.main' : 'primary.main',
                                },
                                '& .MuiSlider-track': {
                                  backgroundColor: errors.LengthOfDefect ? 'error.main' : 'primary.main',
                                },
                              }}
                            />
                            {errors.LengthOfDefect && (
                              <FormHelperText sx={{ color: '#FF4842' }}>{errors.LengthOfDefect}</FormHelperText>
                            )}
                            <Typography variant="body2" color="textSecondary">
                              Selected Length: {values.LengthOfDefect[0]} to {values.LengthOfDefect[1]} Inch
                            </Typography>
                          </FormControl>
                        </Grid>
                        <Grid item width={'100%'}>
                          <TextField
                            required
                            fullWidth
                            size="small"
                            label="Point"
                            type="number"
                            name="Point"
                            onBlur={handleBlur}
                            error={!!errors.Point && touched.Point}
                            helperText={errors.Point && touched.Point ? errors.Point : ''}
                            value={values.Point}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item width={'100%'}>
                          <FormControl size="small" fullWidth component="fieldset">
                            <FormLabel component="legend">Active Status</FormLabel>
                            <RadioGroup row value={values.Active?.trim()} name="Active" onChange={handleChange}>
                              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                            {errors.Active && (
                              <Typography
                                variant="caption"
                                color="error"
                                className={errors.Active ? 'shake-helper-text' : ''}
                              >
                                {errors.Active}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="center" gap={2} sx={{ textAlign: 'center', mt: 3 }}>
                        <Button
                          onClick={submitForm}
                          disabled={isSubmitting}
                          fullWidth
                          variant="contained"
                          color={edit ? 'warning' : 'primary'}
                          size="small"
                          sx={{ boxShadow: 1 }}
                        >
                          {edit ? 'Update' : 'Save'}
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </Stack>
            </Container>
          </Dialog>
        </Grid>
        {/* confirmation dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete} fullScreen={fullScreen} fullWidth maxWidth="xs">
          <DialogTitle id="responsive-dialog-title">Are you sure you want to delete?</DialogTitle>
          <DialogActions>
            <Button variant="contained" autoFocus onClick={handleCloseDelete} color="primary">
              Cancel
            </Button>
            <Button variant="contained" onClick={DeleteFabricDefectPoint} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default FabricDefectPoints;
